/**
 * Colours for chart series, generated rather than mapped.
 *
 * Every report card can be switched to any visualisation it supports, and some
 * of them draw a series per pipeline — a list a manager edits. So there is no
 * fixed set of series to write a colour table against, and a table would be
 * wrong the first time somebody adds a pipeline. Colours are derived from the
 * series' position instead, and a card only has to say how many it needs.
 *
 * Generated in **OKLCH**, not HSL, and that is the whole reason this file is
 * more than ten lines. HSL's lightness is a number about the maths, not about
 * the eye: `hsl(60 100% 50%)` (yellow) and `hsl(240 100% 50%)` (blue) claim the
 * same lightness and one of them is nearly invisible on white while the other
 * is nearly invisible on black. Holding OKLCH's L fixed while rotating hue
 * gives a set that is genuinely evenly bright, so every series in a chart is
 * equally legible and contrast against the card behind it can be chosen once
 * rather than checked colour by colour.
 *
 * Hues step by the golden angle, which is the standard trick for spreading an
 * unknown number of categories: any prefix of the sequence is about as well
 * separated as it could be, so a three-series chart and a nine-series chart are
 * both readable without knowing in advance which one you were going to get.
 */

/** Where the generated ramps start, so the first series is a Pulse colour. */
const BRAND_HUE = 354;

/** ~137.508°. Successive hues never fall close together, at any count. */
const GOLDEN_ANGLE = 137.508;

export const PALETTES = ["brand", "contrast", "pastel", "mono"] as const;
export type PaletteId = (typeof PALETTES)[number];

export const PALETTE_LABELS: Record<PaletteId, string> = {
  brand: "Pulse",
  contrast: "High contrast",
  pastel: "Pastel",
  mono: "Monochrome",
};

/**
 * Okabe–Ito, unchanged.
 *
 * Not generated, because this palette's whole value is that it is the one that
 * has actually been tested against the common forms of colour blindness. A
 * ramp of my own that merely *looked* well separated to me would be a worse
 * answer wearing the same label. Eight is all it has; beyond that the generator
 * takes over, which is honest — nine categories are hard to tell apart by hue
 * for anybody, and the data table is the better answer at that point.
 */
const OKABE_ITO = [
  "#e69f00",
  "#56b4e9",
  "#009e73",
  "#f0e442",
  "#0072b2",
  "#d55e00",
  "#cc79a7",
  "#000000",
];

/** Okabe-Ito's black is invisible on a dark card; its near-white stands in. */
const OKABE_ITO_DARK = [...OKABE_ITO.slice(0, 7), "#f5f5f5"];

interface Ramp {
  /** Perceptual lightness. Chosen per theme so contrast holds on that card. */
  lightness: number;
  /** Colourfulness. Low is pastel, high is poster paint. */
  chroma: number;
}

/*
 * Lighter and less saturated on a dark card, darker and more saturated on a
 * light one. A colour that reads well on white is muddy on near-black, and the
 * usual mistake is to pick one set and let half the charts suffer.
 */
const RAMPS: Record<PaletteId, { light: Ramp; dark: Ramp }> = {
  brand: {
    light: { lightness: 0.58, chroma: 0.16 },
    dark: { lightness: 0.74, chroma: 0.14 },
  },
  contrast: {
    light: { lightness: 0.5, chroma: 0.21 },
    dark: { lightness: 0.78, chroma: 0.19 },
  },
  pastel: {
    light: { lightness: 0.76, chroma: 0.07 },
    dark: { lightness: 0.7, chroma: 0.06 },
  },
  mono: {
    light: { lightness: 0.58, chroma: 0.13 },
    dark: { lightness: 0.74, chroma: 0.12 },
  },
};

/**
 * The lightness monochrome may use, per theme.
 *
 * Stops short of both ends on purpose: pure black on a light card and pure
 * white on a dark one read as chrome rather than as data, and the step either
 * side of them is nearly invisible.
 */
const MONO_RANGE = {
  light: { low: 0.34, high: 0.86 },
  dark: { low: 0.44, high: 0.92 },
};

/**
 * The colour for the series at `index`, in a palette, for a theme.
 *
 * By position rather than by hashing the series name: a hash spreads colours
 * unpredictably, so two adjacent bars can land on near-identical hues and there
 * is nothing anybody can do about it. Position guarantees the spacing, and a
 * series that wants a particular colour can be given one — that is what the
 * overrides in `colorResolver` are for.
 */
export function seriesColor(
  index: number,
  palette: PaletteId,
  dark: boolean,
  /**
   * How many series the chart has.
   *
   * Only monochrome needs it, but it needs it badly: with one hue to work with,
   * lightness is the only thing telling two series apart, and a fixed step size
   * runs off the end of the usable range and hands three of them the same
   * near-white. Knowing the count lets the range be divided instead of stepped
   * through, so eight series are as distinguishable as three.
   */
  count = 8,
): string {
  if (palette === "contrast") {
    const list = dark ? OKABE_ITO_DARK : OKABE_ITO;
    if (index < list.length) return list[index]!;
  }

  const ramp = RAMPS[palette][dark ? "dark" : "light"];

  if (palette === "mono") {
    const steps = Math.max(count, 2);
    /*
     * Alternating in from both ends -- darkest, lightest, second darkest -- so
     * neighbouring series get the two most separated shades left rather than
     * two adjacent ones. A straight ramp puts the hardest pair to tell apart
     * side by side, which is the one arrangement to avoid.
     */
    const half = Math.floor((index % steps) / 2);
    const position =
      index % 2 === 0 ? half : steps - 1 - half;
    const span = MONO_RANGE[dark ? "dark" : "light"];
    const lightness =
      span.low + (position / (steps - 1)) * (span.high - span.low);
    return oklchToHex(lightness, ramp.chroma, BRAND_HUE);
  }

  const hue = (BRAND_HUE + index * GOLDEN_ANGLE) % 360;
  /*
   * A gentle lightness wobble on top of the hue rotation. Past about a dozen
   * series the golden angle starts returning near a hue it has used, and the
   * wobble is what keeps the fourteenth series distinguishable from the first.
   */
  const drift = index >= 12 ? (index % 2 === 0 ? 0.07 : -0.07) : 0;
  return oklchToHex(clamp(ramp.lightness + drift, 0.25, 0.92), ramp.chroma, hue);
}

/**
 * A function that answers "what colour is this series", overrides first.
 *
 * Three sources in order: what the user pinned, what the server already knows
 * (a pipeline's chip colour, which a manager chose and which the rest of Pulse
 * already draws that pipeline in), and finally the generator. Handing back a
 * function rather than an array means a card can ask about a series it only
 * discovers while rendering.
 */
export function colorResolver({
  series,
  overrides,
  palette,
  dark,
}: {
  /** Series keys in a stable order, with any colour the server supplied. */
  series: readonly { key: string; color?: string | null }[];
  /** What the user pinned, by series key. */
  overrides?: Record<string, string>;
  palette: PaletteId;
  dark: boolean;
}): (key: string) => string {
  const positions = new Map(series.map((entry, index) => [entry.key, index]));
  const given = new Map(
    series
      .filter((entry) => typeof entry.color === "string" && entry.color)
      .map((entry) => [entry.key, entry.color as string]),
  );

  return (key: string): string => {
    const pinned = overrides?.[key];
    if (pinned) return pinned;

    /*
     * A pipeline's own colour, but only in the default palette. Somebody who
     * has deliberately switched to high-contrast or monochrome has asked for
     * that scheme, and letting stored chip colours through would half-apply it
     * and look like a bug.
     */
    if (palette === "brand") {
      const supplied = given.get(key);
      if (supplied) return supplied;
    }

    return seriesColor(positions.get(key) ?? 0, palette, dark, series.length);
  };
}

// ── Colour conversion ────────────────────────────────────────────────────────
//
// Written out rather than pulled in. It is about sixty lines of well-specified
// arithmetic, and the alternative is a dependency larger than everything else
// on the page for a page whose only dependency today is `clsx`.

const clamp = (value: number, low: number, high: number): number =>
  Math.min(high, Math.max(low, value));

/**
 * OKLCH to a hex string, brought into sRGB by reducing chroma.
 *
 * Not every OKLCH triple has an sRGB colour — vivid blues especially fall
 * outside it — and the naive fix of clamping the red, green and blue channels
 * shifts hue, which is precisely the property the generator is relying on to
 * keep series apart. Reducing chroma instead walks the colour straight towards
 * grey along its own hue, so it stays the colour it was meant to be, just less
 * saturated. Bisection because the gamut boundary is not something there is a
 * closed form for; twelve steps put it well inside a rounding error of 8-bit.
 */
export function oklchToHex(
  lightness: number,
  chroma: number,
  hue: number,
): string {
  if (inGamut(lightness, chroma, hue)) return hex(lightness, chroma, hue);

  let low = 0;
  let high = chroma;
  for (let step = 0; step < 12; step += 1) {
    const mid = (low + high) / 2;
    if (inGamut(lightness, mid, hue)) low = mid;
    else high = mid;
  }
  return hex(lightness, low, hue);
}

/** OKLCH to linear-light sRGB, which may fall outside 0..1. */
function linearRgb(
  lightness: number,
  chroma: number,
  hue: number,
): [number, number, number] {
  const radians = (hue * Math.PI) / 180;
  const a = chroma * Math.cos(radians);
  const b = chroma * Math.sin(radians);

  const l = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;

  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

function inGamut(lightness: number, chroma: number, hue: number): boolean {
  // A hair of tolerance, so a colour sitting exactly on the boundary is not
  // pushed off it by floating point and desaturated for nothing.
  return linearRgb(lightness, chroma, hue).every(
    (channel) => channel >= -1e-4 && channel <= 1 + 1e-4,
  );
}

/** The sRGB transfer function, then two hex digits per channel. */
function hex(lightness: number, chroma: number, hue: number): string {
  const encoded = linearRgb(lightness, chroma, hue).map((channel) => {
    const value = clamp(channel, 0, 1);
    const gamma =
      value <= 0.0031308 ? 12.92 * value : 1.055 * value ** (1 / 2.4) - 0.055;
    return Math.round(gamma * 255)
      .toString(16)
      .padStart(2, "0");
  });

  return `#${encoded.join("")}`;
}

// ── The picker's colour space ────────────────────────────────────────────────
//
// HSV, not OKLCH. The generator wants perceptual evenness; a person dragging a
// square wants the saturation/value rectangle every design tool has taught them,
// and a perceptual space makes that rectangle a strange shape with unreachable
// corners.

export interface Hsv {
  /** 0..360 */
  h: number;
  /** 0..1 */
  s: number;
  /** 0..1 */
  v: number;
  /** 0..1 */
  a: number;
}

export function hsvToHex({ h, s, v, a }: Hsv): string {
  const f = (n: number): number => {
    const k = (n + h / 60) % 6;
    return v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
  };

  const channels = [f(5), f(3), f(1)].map((channel) =>
    Math.round(clamp(channel, 0, 1) * 255)
      .toString(16)
      .padStart(2, "0"),
  );

  // Alpha is only written when it is not opaque, so an ordinary colour stays a
  // six-digit hex that somebody can read and paste elsewhere.
  const alpha =
    a >= 1
      ? ""
      : Math.round(clamp(a, 0, 1) * 255)
          .toString(16)
          .padStart(2, "0");

  return `#${channels.join("")}${alpha}`;
}

/** Any hex the picker can produce, back to HSV. Null for anything else. */
export function hexToHsv(input: string): Hsv | null {
  const raw = input.trim().replace(/^#/, "");
  const expanded =
    raw.length === 3 || raw.length === 4
      ? raw
          .split("")
          .map((character) => character + character)
          .join("")
      : raw;

  if (!/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(expanded)) return null;

  const [r, g, b] = [0, 2, 4].map(
    (offset) => parseInt(expanded.slice(offset, offset + 2), 16) / 255,
  ) as [number, number, number];
  const a =
    expanded.length === 8 ? parseInt(expanded.slice(6, 8), 16) / 255 : 1;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const span = max - min;

  let h = 0;
  if (span !== 0) {
    if (max === r) h = 60 * (((g - b) / span) % 6);
    else if (max === g) h = 60 * ((b - r) / span + 2);
    else h = 60 * ((r - g) / span + 4);
  }

  return {
    h: (h + 360) % 360,
    s: max === 0 ? 0 : span / max,
    v: max,
    a,
  };
}

/** The same colour written the other two ways, for the picker's text field. */
export function describeColor(hsv: Hsv): { rgb: string; hsl: string } {
  const value = hsvToHex({ ...hsv, a: 1 }).slice(1);
  const [r, g, b] = [0, 2, 4].map((offset) =>
    parseInt(value.slice(offset, offset + 2), 16),
  ) as [number, number, number];

  const lightness = hsv.v * (1 - hsv.s / 2);
  const saturation =
    lightness === 0 || lightness === 1
      ? 0
      : (hsv.v - lightness) / Math.min(lightness, 1 - lightness);

  const round = (n: number): number => Math.round(n * 100);
  const alpha = hsv.a >= 1 ? "" : ` / ${round(hsv.a)}%`;

  return {
    rgb: `rgb(${r} ${g} ${b}${alpha})`,
    hsl: `hsl(${Math.round(hsv.h)} ${round(saturation)}% ${round(lightness)}%${alpha})`,
  };
}
